import { useState } from 'react';
import { useLazyQuery } from '@apollo/client';

import { Dayjs } from 'dayjs';

import { getDate } from '@libs/hooks';
import {
  CardPaymentMerchantEnum,
  CardTransactionTypeEnum,
  TimeUnitEnum,
} from '@libs/backend-common';

import { FiatMethods } from 'src/constants';
import { getCardTransactionsInfoForUserNewQuery } from 'src/gql';
import { useTranslation } from './useTranslation';

const TIME_UNIT_LABELS: Record<TimeUnitEnum, string> = {
  [TimeUnitEnum.SECOND]: 'common_second',
  [TimeUnitEnum.MINUTE]: 'common_minute',
  [TimeUnitEnum.HOUR]: 'common_hour',
};

export const useCheckCardTransactionInfoGe = (
  nextStep: () => void,
  openModal: () => void,
  cardTransactionType: CardTransactionTypeEnum,
  fiatMethod?: FiatMethods,
  merchantName?: CardPaymentMerchantEnum,
  isCardAdd?: boolean,
  isBuyCrypto?: boolean
) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const [date, setDate] = useState<Dayjs>();
  const [currentTimeUnit, setCurrentTimeUnit] = useState<TimeUnitEnum>();
  const [cardOperationWarningText, setCardOperationWarningText] = useState('');

  const triggerLoad = () => setLoading(true);

  const cancelLoad = () => setLoading(false);

  const [getCardTransactionInfo] = useLazyQuery(
    getCardTransactionsInfoForUserNewQuery,
    {
      variables: {
        record: {
          type: cardTransactionType,
          isCardAdd: isCardAdd,
          merchant: merchantName,
        },
      },
      fetchPolicy: 'network-only',
    }
  );

  const handleButtonClick = async () => {
    if (fiatMethod !== FiatMethods.CARD) {
      nextStep();
      return;
    }

    let isAvailable = false;

    try {
      triggerLoad();

      const { data } = await getCardTransactionInfo();

      if (data) {
        const {
          cardTransactionAllowed,
          cardTransactionEnableDate,
          timeAmount,
          timeUnit,
          cardAddAllowed,
          cardDepositAllowed,
        } = data.getCardTransactionsInfoForUser;

        isAvailable = cardTransactionAllowed;

        if (cardTransactionAllowed) {
          nextStep();
        } else {
          openModal();
        }

        if (timeAmount && timeUnit) {
          const formattedUnit = TIME_UNIT_LABELS[timeUnit];

          if (!cardAddAllowed && isCardAdd) {
            setCardOperationWarningText(t('bog_card_add_restriction_text'));
          } else if (!cardDepositAllowed && cardDepositAllowed !== null) {
            const blockedCardDepositText = isBuyCrypto
              ? 'bog_card_buy_crypto_use_blocked'
              : 'bog_card_use_blocked';

            setCardOperationWarningText(
              t(blockedCardDepositText, {
                timeAmount,
                timeUnit: t(formattedUnit, { timeAmount }),
              })
            );
          } else if (cardTransactionType === CardTransactionTypeEnum.WITHDRAW) {
            setCardOperationWarningText(t('card_withdraw_disclaimer_interval'));
          } else {
            setCardOperationWarningText(
              t('card_disclaimer_interval', {
                timeAmount,
                timeUnit: t(formattedUnit, { timeAmount }),
              })
            );
          }
        }

        if (cardTransactionEnableDate) {
          setDate(getDate(cardTransactionEnableDate));
          setCurrentTimeUnit(timeUnit);
        }
      }
      return isAvailable;
    } finally {
      cancelLoad();
    }
  };

  return {
    loading,
    handleButtonClick,
    date,
    timeUnit: currentTimeUnit,
    cardOperationWarningText,
  };
};
